import { FC, useState } from "react";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import {
  CheckOutlined,
  EditOutlined,
  OpenInNewOutlined
} from "@mui/icons-material";

type Props = {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Locked-by-default ClearContract case link, matching the counterparty and broker Legal dialogs.
 * Each agreement row renders its own, so each row locks and unlocks independently.
 */
export const ClearContractCaseField: FC<Props> = ({ value, onChange }) => {
  const [isEditable, setIsEditable] = useState(false);
  const url = buildValidUrl(value);

  return (
    <TextField
      label="Clear Contract case link"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      disabled={!isEditable}
      slotProps={{
        input: {
          startAdornment: url ? (
            <InputAdornment position="start">
              <Tooltip title="Open clear contract case link">
                <IconButton
                  onClick={() =>
                    window.open(url, "_blank", "noopener,noreferrer")
                  }
                  edge="start"
                  color="primary"
                >
                  <OpenInNewOutlined />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip
                title={
                  isEditable
                    ? "Lock clear contract case"
                    : "Unlock clear contract case for editing"
                }
              >
                <IconButton
                  onClick={() => setIsEditable((prev) => !prev)}
                  edge="end"
                  color={isEditable ? "secondary" : "primary"}
                >
                  {isEditable ? <CheckOutlined /> : <EditOutlined />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }
      }}
      autoFocus={isEditable}
      helperText={
        isEditable
          ? "Editing enabled until you lock it again or save the dialog."
          : url
            ? "Disabled for editing. Use the open icon to follow the link."
            : "Locked to reduce accidental changes."
      }
    />
  );
};

const buildValidUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    const url = new URL(trimmedValue);

    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};
